/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Interactive RAG Citations Drawer (Task 10.3.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import { useChatStore, type ChatCitationItem } from '../../stores/useChatStore';
import { useI18n } from '../../hooks/useI18n';
import {
  X,
  Database,
  ExternalLink,
  Copy,
  Check,
  FileText,
  FileCode,
  Globe,
  Sparkles,
  Layers,
  Percent,
  Search,
} from 'lucide-react';

export const CitationsDrawer: React.FC = () => {
  const { t } = useI18n();
  const { activeCitationDrawer, closeCitationDrawer } = useChatStore();

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [copiedSnippet, setCopiedSnippet] = useState<boolean>(false);
  const [copiedFullQuote, setCopiedFullQuote] = useState<boolean>(false);

  useEffect(() => {
    if (activeCitationDrawer) {
      setActiveIndex(activeCitationDrawer.selectedIndex || 0);
    }
  }, [activeCitationDrawer]);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeCitationDrawer) {
        closeCitationDrawer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCitationDrawer, closeCitationDrawer]);

  if (!activeCitationDrawer) return null;

  const citations = activeCitationDrawer.citations || [];
  const currentCitation: ChatCitationItem | undefined = citations[activeIndex] || citations[0];

  const handleCopySnippet = async (text: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopiedSnippet(true);
        setTimeout(() => setCopiedSnippet(false), 2000);
      }
    } catch {
      // Ignored
    }
  };

  const handleCopyFullQuote = async () => {
    if (!currentCitation) return;
    const docName = currentCitation.fileName || currentCitation.title || t('knowledge.title');
    const pageStr = currentCitation.page ? t('chat.pageNumber', { page: String(currentCitation.page) }) : '';
    const formatted = `> "${currentCitation.snippet}"\n\n— *${t('chat.quoteSource', {
      docName,
      pageStr,
      score: String(Math.round((currentCitation.score || 0.95) * 100)),
    })}*`;

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(formatted);
        setCopiedFullQuote(true);
        setTimeout(() => setCopiedFullQuote(false), 2000);
      }
    } catch {
      // Ignored
    }
  };

  const getDocIcon = (name?: string) => {
    if (!name) return FileText;
    const lower = name.toLowerCase();
    if (lower.endsWith('.pdf')) return FileText;
    if (lower.endsWith('.json') || lower.endsWith('.ts') || lower.endsWith('.py')) return FileCode;
    if (lower.startsWith('http://') || lower.startsWith('https://')) return Globe;
    return FileText;
  };

  const DocIcon = getDocIcon(currentCitation?.fileName || currentCitation?.title);
  const similarityScore = Math.round((currentCitation?.score ?? 0.92) * 100);

  return (
    <div className="fixed inset-0 z-50 flex justify-end animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={closeCitationDrawer}
      />

      {/* Slide-over Drawer Panel */}
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-surface-border bg-surface shadow-2xl animate-in slide-in-from-right duration-300">
        {/* Drawer Header */}
        <div className="flex items-center justify-between border-b border-surface-border p-4 bg-surface-elevated/40">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-accent/15 border border-accent/30 flex items-center justify-center text-accent">
              <Database className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-foreground flex items-center gap-2">
                {t('chat.ragCitationsTitle')}
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-accent/10 text-accent font-semibold border border-accent/20">
                  {t('chat.sourcesCount', { count: citations.length })}
                </span>
              </h3>
              <p className="text-[11px] text-foreground-muted">
                {t('chat.vectorizeExcerpt')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={closeCitationDrawer}
            className="p-1.5 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
            title={t('chat.closeDrawer')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Source Navigation Tabs (if multiple citations) */}
        {citations.length > 1 && (
          <div className="flex items-center gap-1.5 p-3 border-b border-surface-border bg-surface/50 overflow-x-auto scrollbar-none">
            {citations.map((c, idx) => {
              const TabIcon = getDocIcon(c.fileName || c.title);
              const isActive = idx === activeIndex;
              const score = Math.round((c.score ?? 0.9) * 100);

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-accent text-accent-fg shadow-sm'
                      : 'bg-surface-elevated border border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <TabIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="max-w-[120px] truncate">
                    {c.fileName || c.title || t('chat.citationNum', { num: idx + 1 })}
                  </span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                      isActive ? 'bg-black/20 text-white' : 'bg-accent/10 text-accent font-semibold'
                    }`}
                  >
                    {score}%
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Drawer Body Scroll Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
          {currentCitation ? (
            <>
              {/* Document Identity Card */}
              <div className="p-4 rounded-2xl border border-surface-border bg-surface-elevated/40 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                      <DocIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-xs text-foreground line-clamp-1">
                        {currentCitation.fileName || currentCitation.title || t('knowledge.title')}
                      </h4>
                      <p className="text-[11px] text-foreground-muted">
                        {currentCitation.page ? `Trang ${currentCitation.page} • ` : ''}
                        {t('chat.citationNum', { num: activeIndex + 1 })}
                      </p>
                    </div>
                  </div>

                  {/* Cosine Match Badge */}
                  <div className="flex flex-col items-end">
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[11px] font-bold font-mono">
                      <Percent className="w-3 h-3" />
                      {t('chat.similarityPercentage', { score: similarityScore })}
                    </span>
                    <span className="text-[9px] text-foreground-muted mt-0.5">
                      Cosine Similarity
                    </span>
                  </div>
                </div>

                {/* Score progress bar */}
                <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-accent rounded-full transition-all duration-500"
                    style={{ width: `${similarityScore}%` }}
                  />
                </div>
              </div>

              {/* Raw Chunk Content Box */}
              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted flex items-center gap-1.5">
                    <Search className="w-3.5 h-3.5 text-accent" />
                    {t('chat.rawChunkHeading')}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleCopySnippet(currentCitation.snippet)}
                    className="flex items-center gap-1 text-[11px] text-foreground-muted hover:text-foreground transition-colors p-1 rounded-lg"
                    title={t('chat.copyCitationBtn')}
                  >
                    {copiedSnippet ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-emerald-500 font-semibold text-[11px]">{t('chat.copiedCitation')}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>{t('common.copy')}</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="p-4 rounded-2xl border border-surface-border bg-surface-elevated/70 text-xs text-foreground leading-relaxed font-sans shadow-inner selection:bg-accent/20 border-l-4 border-l-accent whitespace-pre-wrap">
                  &ldquo;{currentCitation.snippet}&rdquo;
                </div>
              </div>

              {/* RAG Engine Tech Specifications */}
              <div className="space-y-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted flex items-center gap-1.5 px-1">
                  <Layers className="w-3.5 h-3.5 text-primary" />
                  {t('chat.vectorizeSpecs')}
                </span>

                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="p-2.5 rounded-xl border border-surface-border bg-surface/60 space-y-0.5">
                    <span className="text-[10px] text-foreground-muted block">Embedding Model</span>
                    <span className="font-mono font-semibold text-foreground">
                      bge-large-en-v1.5
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl border border-surface-border bg-surface/60 space-y-0.5">
                    <span className="text-[10px] text-foreground-muted block">Vector Dimensions</span>
                    <span className="font-mono font-semibold text-foreground">
                      1536 Float32
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl border border-surface-border bg-surface/60 space-y-0.5">
                    <span className="text-[10px] text-foreground-muted block">Index Storage</span>
                    <span className="font-mono font-semibold text-foreground">
                      Cloudflare Vectorize
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl border border-surface-border bg-surface/60 space-y-0.5">
                    <span className="text-[10px] text-foreground-muted block">Chunk Index ID</span>
                    <span className="font-mono font-semibold text-foreground truncate block">
                      {currentCitation.id || `vec_${Math.abs((currentCitation.snippet.length * 1337) % 999999)}`}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-foreground-muted space-y-2">
              <Database className="w-8 h-8 mx-auto text-foreground-muted/40" />
              <p className="text-xs">{t('chat.noCitationFound')}</p>
            </div>
          )}
        </div>

        {/* Drawer Footer Actions */}
        <div className="p-4 border-t border-surface-border bg-surface-elevated/40 flex items-center gap-2">
          {currentCitation?.sourceUrl && (
            <a
              href={currentCitation.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-surface-border bg-surface text-xs font-semibold text-foreground hover:bg-muted hover:border-primary/40 transition-colors shadow-sm"
            >
              <ExternalLink className="w-3.5 h-3.5 text-primary" />
              <span>{t('chat.openOriginalDoc')}</span>
            </a>
          )}

          <button
            type="button"
            onClick={handleCopyFullQuote}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-accent text-accent-fg hover:opacity-95 text-xs font-semibold shadow-sm transition-opacity"
          >
            {copiedFullQuote ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>{t('chat.copiedCitation')}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span>{t('chat.copyCitationBtn')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
